/**
 * Seeded topic descriptions were written as authoring notes and end with a bare
 * reference URL, e.g.
 *
 *   "Kruskal's (sort edges + DSU) and Prim's (min-heap + visited). …
 *    Reference: https://cp-algorithms.com/graph/mst_kruskal.html"
 *
 * A bare URL inside rich text renders as unclickable literal text, so pull the
 * references out of the prose and let the page render them as real links.
 * Doing it here fixes every existing topic without rewriting 90 rows of content.
 */

const URL_RE = /https?:\/\/[^\s<>"')]+/g;

/** Trailing "Reference:" / "References:" / "Ref:" clause, with its URLs. */
const REFERENCE_CLAUSE_RE = /(?:^|[\s>])(?:References?|Ref)\s*:\s*((?:https?:\/\/[^\s<>"')]+[,;\s]*)+)\s*$/i;

export interface SplitDescription {
  /** The prose with the trailing reference clause removed. */
  html: string;
  /** Absolute URLs pulled out of that clause, de-duplicated, order preserved. */
  references: string[];
}

export function splitReferences(html?: string): SplitDescription {
  if (!html) return { html: '', references: [] };

  // Only strip a reference clause at the very end of the text. A URL used
  // mid-sentence is part of the explanation and must be left alone.
  const trailing = html.replace(/(?:<\/p>|<br\s*\/?>|\s)+$/i, '');
  const match = trailing.match(REFERENCE_CLAUSE_RE);
  if (!match) return { html, references: [] };

  const urls = match[1].match(URL_RE) ?? [];
  if (urls.length === 0) return { html, references: [] };

  const cleaned = trailing.slice(0, match.index).replace(/[\s.,;]+$/, '');
  const closing = html.slice(trailing.length);

  return {
    html: cleaned ? `${cleaned}${/<\/p>\s*$/i.test(closing) ? closing : ''}` : '',
    references: [...new Set(urls.map((u) => u.replace(/[.,;)]+$/, '')))],
  };
}

/** "cp-algorithms.com" — a readable label for a reference chip. */
export function hostLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}
